import React, { useMemo, useState } from "react";
import {
  Button,
  InputField,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DatePicker,
  DismissibleTag,
} from "@jem-open/jem-ui";
import { AlertCircle, Loader2 } from "lucide-react";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs from "dayjs";
import { SegmentBuilderRule, createEmptyBuilderRule } from "../services/model/SegmentRule";
import McmAvailableField from "../services/model/McmAvailableField";
import CreateMcmSegmentRequest from "../services/request/CreateMcmSegmentRequest";
import useMcmSegmentPreviewApiService from "../services/api/McmSegmentPreviewApiService";

type SegmentBuilderProps = {
  availableFields: McmAvailableField[];
  targetCompanyId: number;
  targetCompanyName?: string;
  onSave: (request: CreateMcmSegmentRequest) => void;
  onCancel: () => void;
  isSaving?: boolean;
  defaultName?: string;
  defaultDescription?: string;
  defaultRules?: SegmentBuilderRule[];
  defaultLogicOperator?: "AND" | "OR";
  saveLabel?: string;
  matchedEmployeeCount?: number | null;
};

const SegmentBuilder: React.FC<SegmentBuilderProps> = ({
  availableFields,
  targetCompanyId,
  targetCompanyName,
  onSave,
  onCancel,
  isSaving,
  defaultName,
  defaultDescription,
  defaultRules,
  defaultLogicOperator,
  saveLabel,
  matchedEmployeeCount,
}) => {
  const [rules, setRules] = useState<SegmentBuilderRule[]>(
    defaultRules && defaultRules.length > 0 ? defaultRules : [createEmptyBuilderRule()],
  );
  const [tagInput, setTagInput] = useState<Record<string, string>>({});
  const [segmentName, setSegmentName] = useState(defaultName || "");
  const [segmentDescription, setSegmentDescription] = useState(defaultDescription || "");
  const [logicOperator, setLogicOperator] = useState<"AND" | "OR">(defaultLogicOperator || "OR");

  const filledRules = rules.filter(
    (r) => r.field && r.operator && r.values.some((v) => v.trim().length > 0),
  );
  const canSave = segmentName.trim().length > 0 && filledRules.length > 0;

  // Live preview: debounce rules and logicOperator, then call preview API
  const [debouncedRules] = useDebouncedValue(rules, 500);
  const [debouncedLogicOperator] = useDebouncedValue(logicOperator, 500);

  const hasValidRules = filledRules.length > 0;

  const debouncedFilledRules = debouncedRules.filter(
    (r) => r.field && r.operator && r.values.some((v) => v.trim().length > 0),
  );
  const isDebouncedValid = debouncedFilledRules.length > 0;

  const debouncedFilledRulesKey = JSON.stringify(debouncedFilledRules);
  const previewRequest = useMemo(
    () => ({
      target_company_id: targetCompanyId,
      rules: debouncedFilledRules.map((r) => ({
        field: r.field,
        operator: r.operator,
        value: r.values.length === 1 ? r.values[0] : r.values.join(","),
      })),
      logic_operator: debouncedLogicOperator,
    }),
    [targetCompanyId, debouncedFilledRulesKey, debouncedLogicOperator], // eslint-disable-line
  );

  const previewQuery = useMcmSegmentPreviewApiService(previewRequest, isDebouncedValid);

  const getFieldDef = (field: string): McmAvailableField | undefined =>
    availableFields.find((f) => f.field === field);

  const getValueType = (field: string): SegmentBuilderRule["valueType"] => {
    const def = getFieldDef(field);
    if (!def) return "text";
    if (def.type === "date") return "date";
    if (def.options && def.options.length > 0) return "select";
    return "text";
  };

  const addRule = (): void => {
    setRules((prev) => [...prev, createEmptyBuilderRule()]);
  };

  const removeRule = (localId: string): void => {
    if (rules.length <= 1) return;
    setRules((prev) => prev.filter((r) => r.localId !== localId));
  };

  const updateRule = (localId: string, updates: Partial<SegmentBuilderRule>): void => {
    setRules((prev) =>
      prev.map((r) => (r.localId === localId ? { ...r, ...updates } : r))
    );
  };

  const addTagValue = (ruleLocalId: string, value: string): void => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setRules((prev) =>
      prev.map((r) =>
        r.localId === ruleLocalId && !r.values.includes(trimmed)
          ? { ...r, values: [...r.values, trimmed] }
          : r
      )
    );
    setTagInput((prev) => ({ ...prev, [ruleLocalId]: "" }));
  };

  const removeTagValue = (ruleLocalId: string, value: string): void => {
    setRules((prev) =>
      prev.map((r) =>
        r.localId === ruleLocalId
          ? { ...r, values: r.values.filter((v) => v !== value) }
          : r
      )
    );
  };

  const handleSave = (): void => {
    const apiRules = filledRules.map((r) => ({
      field: r.field,
      operator: r.operator,
      value: r.values.length === 1 ? r.values[0] : r.values.join(","),
    }));
    onSave({
      name: segmentName.trim(),
      description: segmentDescription,
      rules: apiRules,
      logic_operator: logicOperator,
      target_company_id: targetCompanyId,
    });
  };

  return (
    <div className="border-t border-greyscale-border px-md py-sm bg-white">
      <div className="flex flex-col gap-sm">
        {/* Target company (read-only) */}
        {targetCompanyName && (
          <div className="flex flex-col gap-xxxs">
            <p className="text-sm font-semibold text-greyscale-text-title">
              Target Company
            </p>
            <p className="text-sm text-greyscale-text-body px-xs py-xxs bg-greyscale-bg-subtle rounded-md inline-block w-fit">
              {targetCompanyName}
            </p>
          </div>
        )}

        {/* Segment name */}
        <div className="flex flex-col gap-xxxs">
          <p className="text-sm font-semibold text-greyscale-text-title">
            Segment name <span className="text-red-500">*</span>
          </p>
          <InputField
            value={segmentName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSegmentName(e.target.value)}
            placeholder="e.g. Managers"
          />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-xxxs">
          <p className="text-sm font-semibold text-greyscale-text-title">
            Description
          </p>
          <InputField
            value={segmentDescription}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSegmentDescription(e.target.value)}
            placeholder="Brief description of this segment"
          />
        </div>

        {rules.map((rule, index) => (
          <div key={rule.localId}>
            {/* Logic operator divider */}
            {index > 0 && (
              <div className="py-xs">
                <span className="text-sm font-semibold text-greyscale-text-title">{logicOperator}</span>
              </div>
            )}

            {/* Inputs row with labels */}
            <div className="grid grid-cols-[1fr_1fr_2fr_auto] gap-xs items-end">
              {/* Field */}
              <div className="flex flex-col gap-xxxs">
                <span className="text-sm font-semibold text-greyscale-text-title">
                  Field <span className="text-red-500">*</span>
                </span>
                <Select
                  value={rule.field}
                  onValueChange={(val: string) => {
                    const vt = getValueType(val);
                    updateRule(rule.localId, { field: val, operator: "", values: [], valueType: vt });
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a field" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFields.map((f) => (
                      <SelectItem key={f.field} value={f.field}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Rule / Operator */}
              <div className="flex flex-col gap-xxxs">
                <span className="text-sm font-semibold text-greyscale-text-title">
                  Rule <span className="text-red-500">*</span>
                </span>
                <Select
                  value={rule.operator}
                  onValueChange={(val: string) => {
                    const vt = getValueType(rule.field);
                    updateRule(rule.localId, { operator: val, valueType: vt, values: [] });
                  }}
                  disabled={!rule.field}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select rule" />
                  </SelectTrigger>
                  <SelectContent>
                    {(getFieldDef(rule.field)?.operators || []).map((op) => (
                      <SelectItem key={op} value={op}>
                        {op}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Value */}
              <div className="flex flex-col gap-xxxs">
                <span className="text-sm font-semibold text-greyscale-text-title">
                  Value <span className="text-red-500">*</span>
                </span>
                {rule.valueType === "select" && (
                  <div className="flex items-center gap-xxs border border-greyscale-border rounded-lg px-xs h-[46px] flex-wrap overflow-hidden">
                    {rule.values.map((v) => (
                      <DismissibleTag
                        key={v}
                        variant="navy"
                        onDismiss={() => removeTagValue(rule.localId, v)}
                      >
                        {v}
                      </DismissibleTag>
                    ))}
                    <input
                      value={tagInput[rule.localId] || ""}
                      onChange={(e) => setTagInput((prev) => ({ ...prev, [rule.localId]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTagValue(rule.localId, tagInput[rule.localId] || "");
                        }
                      }}
                      placeholder={rule.values.length === 0 ? "Type and press Enter" : ""}
                      disabled={!rule.operator}
                      className="flex-1 min-w-[100px] text-sm outline-none bg-transparent"
                    />
                  </div>
                )}
                {rule.valueType === "text" && (
                  <InputField
                    value={rule.values[0] || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRule(rule.localId, { values: [e.target.value] })}
                    placeholder="Open text field"
                    disabled={!rule.operator}
                  />
                )}
                {rule.valueType === "date" && (
                  <div className="flex items-center gap-xs">
                    <DatePicker
                      date={rule.values[0] ? dayjs(rule.values[0]).toDate() : undefined}
                      onDateChange={(d: Date | undefined) =>
                        updateRule(rule.localId, {
                          values: [d ? dayjs(d).format("YYYY-MM-DD") : "", rule.values[1] || ""],
                        })
                      }
                      placeholder="Start date"
                      disabled={!rule.operator}
                      className="flex-1"
                    />
                    <DatePicker
                      date={rule.values[1] ? dayjs(rule.values[1]).toDate() : undefined}
                      onDateChange={(d: Date | undefined) =>
                        updateRule(rule.localId, {
                          values: [rule.values[0] || "", d ? dayjs(d).format("YYYY-MM-DD") : ""],
                        })
                      }
                      placeholder="End date"
                      disabled={!rule.operator}
                      className="flex-1"
                    />
                  </div>
                )}
              </div>

              {/* Add / Remove buttons */}
              <div className="flex items-center gap-xxs self-end mb-[1px]">
                <Button size="sm" onClick={addRule}>
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeRule(rule.localId)}
                  disabled={rules.length <= 1}
                >
                  Remove
                </Button>
              </div>
            </div>
          </div>
        ))}

        {/* Logic operator toggle */}
        {rules.length > 1 && (
          <div className="flex items-center gap-xs">
            <span className="text-sm text-greyscale-text-body">Logic:</span>
            <Button
              size="sm"
              variant={logicOperator === "AND" ? "default" : "outline"}
              onClick={() => setLogicOperator("AND")}
            >
              AND
            </Button>
            <Button
              size="sm"
              variant={logicOperator === "OR" ? "default" : "outline"}
              onClick={() => setLogicOperator("OR")}
            >
              OR
            </Button>
          </div>
        )}

        {/* Estimated match banner — only visible once rules are complete */}
        {hasValidRules && (
          <div className="flex items-center gap-xs px-md py-sm bg-[#FFF4ED] rounded-md border border-[#FDDCBB] mt-xs">
            <AlertCircle size={18} className="text-[#E5722E]" />
            <span className="text-sm font-semibold text-[#E5722E]">
              {(!isDebouncedValid || previewQuery.isFetching) && (
                <span className="inline-flex items-center gap-xxs">
                  Calculating estimated match...
                  <Loader2 size={14} className="animate-spin" />
                </span>
              )}
              {isDebouncedValid &&
                !previewQuery.isFetching &&
                previewQuery.isError &&
                "Unable to calculate estimated match right now"}
              {isDebouncedValid &&
                !previewQuery.isFetching &&
                !previewQuery.isError &&
                previewQuery.data &&
                `Estimated match: ${previewQuery.data.matched_count} ${previewQuery.data.matched_count === 1 ? "Employee" : "Employees"}`}
            </span>
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex items-center justify-end gap-xs pt-xs">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !canSave}>
            {isSaving ? "Saving..." : saveLabel || "Save Segment"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SegmentBuilder;
